import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Briefcase,
  Cake,
  Check,
  Download,
  Droplet,
  FileDown,
  Heart,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Ruler,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Syringe,
  TestTube2,
  User,
  X,
} from "lucide-react";
import { patientFromUser, usePatientAuth } from "../../store/authPatientStore";
import { cn, downloadFileFromUrl } from "../../lib/utils";
import PatientPageHeading from "../../components/organisms/patient-dashboard/PatientPageHeading";
import {
  getPatientMedicalFile,
  getMedicalFilePdf,
  getVaccinationCertificatePdf,
  getProfile,
  updatePatient,
  type PatientMedicalFile,
  type PatientMedicalFilePersonalInfo,
  type UpdatePatientData,
} from "../../api/authPatient";
import {
  getBeninArrondissements,
  getBeninCommunes,
  getBeninDepartements,
  getProfessionsMetiers,
  type BeninArrondissement,
  type BeninCommune,
  type BeninDepartement,
  type ProfessionMetier,
} from "../../api/publicBenin";

type CoverageStatus = "verification" | "non" | "oui";

type MedicalSectionId = "amo" | "personnel" | "medical" | "documents";

const MEDICAL_SECTIONS: { id: MedicalSectionId; label: string; icon: typeof Shield }[] = [
  { id: "amo", label: "Couverture AMO", icon: Shield },
  { id: "personnel", label: "Informations personnelles", icon: User },
  { id: "medical", label: "Informations médicales", icon: Heart },
  { id: "documents", label: "Export & vaccinations", icon: FileDown },
];

function SectionHeader({ icon: Icon, title }: { icon: typeof Shield; title: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-[#DAF1E9]">
        <Icon size={18} className="text-[#00553E]" />
      </span>
      <h3 className="text-sm font-semibold text-[#0B1F16]">{title}</h3>
    </div>
  );
}

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

function validatePatientProfileForm(form: PatientMedicalFilePersonalInfo): string | null {
  const nom = (form.nom ?? "").trim();
  const prenom = (form.prenom ?? "").trim();
  const email = (form.email ?? "").trim();
  const telephone = (form.telephone ?? "").trim();
  if (nom.length < 2) return "Le nom doit contenir au moins 2 caractères.";
  if (prenom.length < 2) return "Le prénom doit contenir au moins 2 caractères.";
  if (!email) return "L'email est obligatoire.";
  if (!EMAIL_RE.test(email)) return "Adresse e-mail invalide.";
  if (telephone.length > 0 && telephone.length < 8) return "Le téléphone doit contenir au moins 8 caractères.";
  return null;
}

export default function PatientMedicalFile() {
  const navigate = useNavigate();
  const { patient, activeProfil, setPatient } = usePatientAuth();
  const [medicalFile, setMedicalFile] = useState<PatientMedicalFile | null>(null);
  const [form, setForm] = useState<PatientMedicalFilePersonalInfo>({
    nom: "",
    prenom: "",
    adresse: "",
    email: "",
    telephone: "",
    profession: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profession/Adresse : mêmes référentiels que la création de patient côté établissement
  // (AddPatient.tsx) — listes déroulantes, pas de texte libre. `adresse` est composée
  // (Département-Commune-Arrondissement-Quartier) comme partout ailleurs dans le produit.
  const [metiers, setMetiers] = useState<ProfessionMetier[]>([]);
  const [loadingMetiers, setLoadingMetiers] = useState(false);
  const [professionAutreTexte, setProfessionAutreTexte] = useState("");
  const [departements, setDepartements] = useState<BeninDepartement[]>([]);
  const [departementId, setDepartementId] = useState("");
  const [communes, setCommunes] = useState<BeninCommune[]>([]);
  const [communeNom, setCommuneNom] = useState("");
  const [arrondissements, setArrondissements] = useState<BeninArrondissement[]>([]);
  const [arrondissementNom, setArrondissementNom] = useState("");
  const [quartierLibre, setQuartierLibre] = useState("");
  const [loadingDepartements, setLoadingDepartements] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  const [loadingArrondissements, setLoadingArrondissements] = useState(false);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [vaccinationPdfLoading, setVaccinationPdfLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeSection, setActiveSection] = useState<MedicalSectionId>("personnel");
  const patientNip = activeProfil?.nip?.trim() || patient?.nip?.trim() || "";

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!patientNip) {
        if (!mounted) return;
        setMedicalFile(null);
        setError("NIP patient introuvable. Impossible de charger le dossier médical.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      const result = await getPatientMedicalFile(patientNip);
      if (!mounted) return;

      if (!result.ok) {
        setMedicalFile(null);
        setError(result.message || "Impossible de charger le dossier médical.");
        setLoading(false);
        return;
      }

      setMedicalFile(result.data);
      setForm({
        nom: result.data.informationsPersonnelles?.nom ?? "",
        prenom: result.data.informationsPersonnelles?.prenom ?? "",
        adresse: result.data.informationsPersonnelles?.adresse ?? "",
        email: result.data.informationsPersonnelles?.email ?? "",
        telephone: result.data.informationsPersonnelles?.telephone ?? "",
        profession: result.data.informationsPersonnelles?.profession ?? "",
      });
      setLoading(false);
    }

    void load();
    return () => {
      mounted = false;
    };
  }, [patientNip]);

  useEffect(() => {
    let mounted = true;
    setLoadingMetiers(true);
    getProfessionsMetiers().then((result) => {
      if (!mounted) return;
      if (result.ok) setMetiers(result.data);
      setLoadingMetiers(false);
    });
    setLoadingDepartements(true);
    getBeninDepartements().then((result) => {
      if (!mounted) return;
      if (result.ok) setDepartements(result.data);
      setLoadingDepartements(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  /**
   * Bascule le select "Profession" en mode "Autre" dès que sa valeur (venant du dossier chargé,
   * d'un rechargement après sauvegarde, ou d'une annulation d'édition) ne correspond à aucun slug
   * connu du référentiel — cas des anciennes valeurs en texte libre saisies avant ce correctif.
   * Attend que `metiers` soit chargé pour ne pas basculer prématurément en "Autre" pendant le
   * chargement initial du référentiel.
   */
  useEffect(() => {
    if (loadingMetiers || metiers.length === 0) return;
    const raw = form.profession;
    if (!raw || raw === "autre") return;
    const isKnownSlug = metiers.some((m) => m.slug === raw);
    if (!isKnownSlug) {
      setProfessionAutreTexte(raw);
      setForm((f) => ({ ...f, profession: "autre" }));
    }
  }, [form.profession, metiers, loadingMetiers]);

  useEffect(() => {
    let mounted = true;
    if (!departementId) {
      setCommunes([]);
      return () => {
        mounted = false;
      };
    }
    setLoadingCommunes(true);
    setAddressError(null);
    getBeninCommunes(departementId)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setCommunes([]);
          setAddressError(result.message || "Impossible de charger les communes.");
          return;
        }
        setCommunes(result.data);
      })
      .finally(() => {
        if (mounted) setLoadingCommunes(false);
      });
    return () => {
      mounted = false;
    };
  }, [departementId]);

  useEffect(() => {
    let mounted = true;
    if (!departementId || !communeNom) {
      setArrondissements([]);
      return () => {
        mounted = false;
      };
    }
    setLoadingArrondissements(true);
    setAddressError(null);
    getBeninArrondissements(departementId, communeNom)
      .then((result) => {
        if (!mounted) return;
        if (!result.ok) {
          setArrondissements([]);
          setAddressError(result.message || "Impossible de charger les arrondissements.");
          return;
        }
        setArrondissements(result.data);
      })
      .finally(() => {
        if (mounted) setLoadingArrondissements(false);
      });
    return () => {
      mounted = false;
    };
  }, [departementId, communeNom]);

  /**
   * `personal.profession` stocke le slug du métier (ex. "cadres_professions_intellectuelles"),
   * pas son libellé — voir le calcul de `professionLabel` plus bas, une fois `personal` déclaré.
   */
  const departementNom = useMemo(
    () => departements.find((d) => d.id === departementId)?.nom ?? "",
    [departements, departementId]
  );
  const composedAdresse = useMemo(
    () =>
      [departementNom, communeNom, arrondissementNom, quartierLibre]
        .map((segment) => segment.trim())
        .filter(Boolean)
        .join("-"),
    [departementNom, communeNom, arrondissementNom, quartierLibre]
  );

  /**
   * Adresse existante en texte libre (ex. "Cotonou, Bénin", saisie avant ce correctif) : pas de
   * décomposition fiable possible en département/commune/arrondissement. On la préserve intégrale
   * dans "Quartier" (rien n'est perdu) plutôt que de forcer une correspondance approximative ;
   * le patient choisit ensuite Département/Commune/Arrondissement pour la structurer.
   */
  const startEditingAddress = (existingAdresse: string) => {
    setDepartementId("");
    setCommuneNom("");
    setArrondissementNom("");
    setQuartierLibre(existingAdresse.trim());
  };

  const fullName = useMemo(
    () =>
      `${medicalFile?.informationsPersonnelles?.nom ?? activeProfil?.nom ?? patient?.nom ?? ""} ${medicalFile?.informationsPersonnelles?.prenom ?? activeProfil?.prenom ?? patient?.prenom ?? ""}`.trim(),
    [
      medicalFile?.informationsPersonnelles?.nom,
      medicalFile?.informationsPersonnelles?.prenom,
      activeProfil?.nom,
      activeProfil?.prenom,
      patient?.nom,
      patient?.prenom,
    ]
  );
  const coverage = (medicalFile?.snapshot?.coverage ?? "verification") as CoverageStatus;
  const age = useMemo(() => {
    const birth: string | undefined =
      medicalFile?.informationsPersonnelles?.dateNaissance ?? medicalFile?.snapshot?.birth;
    if (!birth) return undefined;
    const birthDate = new Date(birth);
    if (Number.isNaN(birthDate.getTime())) return undefined;
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) years--;
    return years;
  }, [medicalFile]);

  const personal = medicalFile?.informationsPersonnelles;
  const medical = medicalFile?.informationsMedicales;
  const snapshot = medicalFile?.snapshot;

  /**
   * `personal.profession` stocke le slug du métier (ex. "cadres_professions_intellectuelles"),
   * pas son libellé — même convention que AddPatient.tsx. Repli sur la valeur brute si le
   * référentiel n'a pas encore chargé ou pour d'anciennes valeurs en texte libre saisies avant
   * ce correctif (aucun slug ne correspond alors, on affiche tel quel plutôt que de le masquer).
   */
  const professionLabel = useMemo(() => {
    const raw = personal?.profession;
    if (!raw) return "";
    return metiers.find((m) => m.slug === raw)?.title ?? raw;
  }, [personal?.profession, metiers]);

  const downloadMedicalPdf = async () => {
    if (!patientNip || pdfLoading) return;
    setPdfLoading(true);
    setError("");
    const result = await getMedicalFilePdf(patientNip);
    setPdfLoading(false);
    if (!result.ok) {
      setError(result.message || "Impossible de générer le PDF du dossier.");
      return;
    }
    await downloadFileFromUrl(result.pdfUrl, `dossier-medical-${patientNip}.pdf`);
  };

  const downloadVaccinationPdf = async () => {
    if (!patientNip || vaccinationPdfLoading) return;
    setVaccinationPdfLoading(true);
    setError("");
    const result = await getVaccinationCertificatePdf(patientNip);
    setVaccinationPdfLoading(false);
    if (!result.ok) {
      setError(result.message || "Impossible de générer le PDF du carnet de vaccination.");
      return;
    }
    await downloadFileFromUrl(result.pdfUrl, `carnet-vaccination-${patientNip}.pdf`);
  };

  const onSave = async () => {
    if (!patientNip || saving) return;
    const validationError = validatePatientProfileForm(form);
    if (validationError) {
      setError(validationError);
      setSuccess("");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    const professionValue =
      form.profession === "autre" ? professionAutreTexte.trim() : form.profession?.trim();
    const body: UpdatePatientData = {
      nom: form.nom?.trim(),
      prenom: form.prenom?.trim(),
      adresse: composedAdresse,
      email: form.email?.trim(),
      telephone: form.telephone?.trim(),
      profession: professionValue,
    };
    const result = await updatePatient(patientNip, body);
    setSaving(false);
    if (!result.ok) {
      setError(result.message || "Impossible de mettre à jour les informations personnelles.");
      return;
    }
    const reload = await getPatientMedicalFile(patientNip);
    if (!reload.ok) {
      setError(reload.message || "Profil mis à jour mais impossible de recharger le dossier médical.");
      setEditing(false);
      return;
    }
    setMedicalFile(reload.data);
    setForm({
      nom: reload.data.informationsPersonnelles?.nom ?? "",
      prenom: reload.data.informationsPersonnelles?.prenom ?? "",
      adresse: reload.data.informationsPersonnelles?.adresse ?? "",
      email: reload.data.informationsPersonnelles?.email ?? "",
      telephone: reload.data.informationsPersonnelles?.telephone ?? "",
      profession: reload.data.informationsPersonnelles?.profession ?? "",
    });
    const profileRes = await getProfile();
    if (profileRes.ok) {
      const nextPatient = patientFromUser(profileRes.data);
      if (nextPatient) setPatient(nextPatient);
    }
    setEditing(false);
    setSuccess("Informations personnelles mises à jour.");
  };

  return (
    <div className="space-y-6">
      <PatientPageHeading
        title="Votre dossier médical"
        description={fullName ? `Consultation du dossier de ${fullName}` : "Consultation de votre dossier"}
        actions={
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold",
              coverage === "oui" && "bg-[#E8F4EF] text-[#0F9D58]",
              coverage === "non" && "bg-red-50 text-[#D92D20]",
              coverage === "verification" && "bg-amber-50 text-[#C98A00]",
            )}
          >
            {coverage === "oui" ? (
              <ShieldCheck size={14} />
            ) : coverage === "non" ? (
              <ShieldAlert size={14} />
            ) : (
              <Shield size={14} />
            )}
            {coverage === "oui" ? "AMO couvert" : coverage === "non" ? "AMO non couvert" : "AMO en vérification"}
          </span>
        }
      />

      <div className="rounded-[12px] border bg-white p-5 md:p-6 space-y-6" style={{ borderColor: "#E5E7EB" }}>
        {loading && <p className="text-sm text-gray-500">Chargement du dossier médical…</p>}
        {!loading && error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">{error}</div>
        )}
        {success && (
          <div className="text-sm text-[#00553E] bg-[#E8F4EF] border border-[#CDE5DB] rounded-[10px] px-3 py-2">{success}</div>
        )}
        {!loading && !error && !medicalFile && (
          <div className="text-sm text-gray-600 bg-gray-50 border rounded-[10px] px-3 py-2" style={{ borderColor: "#E5E7EB" }}>
            Aucun dossier médical disponible pour le moment.
          </div>
        )}
        {!loading && !error && medicalFile && medicalFile.existe === false && (
          <div
            className="flex flex-col items-center gap-2 rounded-[10px] border bg-[#FAFBFB] px-4 py-10 text-center"
            style={{ borderColor: "#E5E7EB" }}
          >
            <FileDown size={28} className="text-gray-400" />
            <p className="text-sm font-semibold text-[#0B1F16]">Aucun dossier médical n'a encore été créé</p>
            <p className="max-w-md text-sm text-gray-500">
              Il sera rempli par un professionnel de santé lors de votre prochaine consultation.
            </p>
          </div>
        )}
        {!loading && !error && medicalFile && medicalFile.existe !== false && (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
            <nav
              className="flex shrink-0 gap-2 overflow-x-auto pb-1 lg:w-56 lg:flex-col lg:overflow-visible lg:pb-0 lg:pr-0"
              aria-label="Sections du dossier médical"
            >
              {MEDICAL_SECTIONS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveSection(id)}
                  className={cn(
                    "flex min-w-[max-content] items-center gap-2 rounded-[10px] border px-3 py-2.5 text-left text-sm font-medium transition-colors lg:min-w-0 lg:w-full",
                    activeSection === id
                      ? "border-[#00553E] bg-[#E8F4EF] text-[#00553E] shadow-sm"
                      : "border-transparent bg-[#FAFBFB] text-[#0B1F16] hover:border-[#E5E7EB] hover:bg-white"
                  )}
                  style={activeSection !== id ? { borderColor: "transparent" } : undefined}
                >
                  <Icon className="size-4 shrink-0 opacity-90" aria-hidden />
                  <span className="leading-snug">{label}</span>
                </button>
              ))}
            </nav>

            <div
              className="min-h-[280px] flex-1 rounded-[12px] border bg-white p-4 md:p-6"
              style={{ borderColor: "#E5E7EB" }}
            >
              {activeSection === "amo" && (
                <div className="space-y-4">
                  <SectionHeader icon={Shield} title="Couverture AMO" />
                  <p className="text-sm text-gray-600">
                    Statut de votre assurance maladie obligatoire telle qu’indiquée sur votre dossier.
                  </p>
                  <div
                    className={cn(
                      "flex items-center gap-3 rounded-[10px] border p-4",
                      coverage === "oui" && "border-[#CDE5DB] bg-[#E8F4EF]",
                      coverage === "non" && "border-red-200 bg-red-50",
                      coverage === "verification" && "border-amber-200 bg-amber-50",
                    )}
                  >
                    {coverage === "oui" ? (
                      <ShieldCheck size={22} className="shrink-0 text-[#0F9D58]" />
                    ) : coverage === "non" ? (
                      <ShieldAlert size={22} className="shrink-0 text-[#D92D20]" />
                    ) : (
                      <Shield size={22} className="shrink-0 text-[#C98A00]" />
                    )}
                    <span
                      className={cn(
                        "text-sm font-semibold",
                        coverage === "oui" && "text-[#0F9D58]",
                        coverage === "non" && "text-[#D92D20]",
                        coverage === "verification" && "text-[#C98A00]",
                      )}
                    >
                      {coverage === "oui" ? "Couvert" : coverage === "non" ? "Non couvert" : "Vérification en cours"}
                    </span>
                  </div>
                </div>
              )}

              {activeSection === "personnel" && (
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <SectionHeader icon={User} title="Informations personnelles" />
                    {editing ? (
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(false);
                            setForm({
                              nom: personal?.nom ?? "",
                              prenom: personal?.prenom ?? "",
                              adresse: personal?.adresse ?? "",
                              email: personal?.email ?? "",
                              telephone: personal?.telephone ?? "",
                              profession: personal?.profession ?? "",
                            });
                            startEditingAddress(personal?.adresse ?? "");
                          }}
                          className="inline-flex h-9 items-center gap-1.5 rounded-[6px] border px-3 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                          style={{ borderColor: "#E5E7EB" }}
                        >
                          <X size={14} />
                          Annuler
                        </button>
                        <button
                          type="button"
                          onClick={() => void onSave()}
                          disabled={saving}
                          className="inline-flex h-9 items-center gap-1.5 rounded-[6px] bg-[#00553E] px-3 text-sm font-semibold text-white disabled:opacity-60"
                        >
                          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                          {saving ? "Enregistrement…" : "Enregistrer"}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(true);
                          startEditingAddress(form.adresse ?? "");
                        }}
                        className="inline-flex h-9 items-center gap-1.5 rounded-[6px] border px-3 text-sm font-semibold text-[#00553E] hover:bg-[#F4FAF8]"
                        style={{ borderColor: "#CDE5DB" }}
                      >
                        <Pencil size={14} />
                        Modifier
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                    <div>
                      <div className="text-gray-500">Nom</div>
                      {editing ? (
                        <input
                          value={form.nom ?? ""}
                          onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                          className="mt-1 h-10 w-full rounded-[10px] border px-3"
                          style={{ borderColor: "#E5E7EB" }}
                        />
                      ) : (
                        <div className="mt-1 font-semibold text-[#0B1F16]">{personal?.nom || "—"}</div>
                      )}
                    </div>
                    <div>
                      <div className="text-gray-500">Prénom</div>
                      {editing ? (
                        <input
                          value={form.prenom ?? ""}
                          onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))}
                          className="mt-1 h-10 w-full rounded-[10px] border px-3"
                          style={{ borderColor: "#E5E7EB" }}
                        />
                      ) : (
                        <div className="mt-1 font-semibold text-[#0B1F16]">{personal?.prenom || "—"}</div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Cake size={12} /> Âge
                      </div>
                      <div className="mt-1 font-semibold text-[#0B1F16]">{age != null ? `${age} ans` : "—"}</div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Mail size={12} /> Email
                      </div>
                      {editing ? (
                        <input
                          value={form.email ?? ""}
                          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                          className="mt-1 h-10 w-full rounded-[10px] border px-3"
                          style={{ borderColor: "#E5E7EB" }}
                        />
                      ) : (
                        <div className="mt-1 font-semibold text-[#0B1F16] break-all">{personal?.email || "—"}</div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Phone size={12} /> Téléphone
                      </div>
                      {editing ? (
                        <input
                          value={form.telephone ?? ""}
                          onChange={(e) => setForm((f) => ({ ...f, telephone: e.target.value }))}
                          className="mt-1 h-10 w-full rounded-[10px] border px-3"
                          style={{ borderColor: "#E5E7EB" }}
                        />
                      ) : (
                        <div className="mt-1 font-semibold text-[#0B1F16]">{personal?.telephone || "—"}</div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Briefcase size={12} /> Profession
                      </div>
                      {editing ? (
                        <>
                          <select
                            value={form.profession ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setForm((f) => ({ ...f, profession: value }));
                              if (value !== "autre") setProfessionAutreTexte("");
                            }}
                            disabled={loadingMetiers}
                            className="mt-1 h-10 w-full rounded-[10px] border bg-white px-3 disabled:opacity-70"
                            style={{ borderColor: "#E5E7EB" }}
                          >
                            <option value="">{loadingMetiers ? "Chargement…" : "Sélectionner une profession"}</option>
                            {metiers.map((m) => (
                              <option key={m.slug} value={m.slug}>{m.title}</option>
                            ))}
                          </select>
                          {form.profession === "autre" && (
                            <input
                              value={professionAutreTexte}
                              onChange={(e) => setProfessionAutreTexte(e.target.value)}
                              placeholder="Précisez la profession"
                              className="mt-2 h-10 w-full rounded-[10px] border px-3"
                              style={{ borderColor: "#E5E7EB" }}
                            />
                          )}
                        </>
                      ) : (
                        <div className="mt-1 font-semibold text-[#0B1F16]">{professionLabel || "—"}</div>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <MapPin size={12} /> Adresse
                      </div>
                      {editing ? (
                        <div className="mt-1 space-y-2">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <select
                              value={departementId}
                              onChange={(e) => {
                                setDepartementId(e.target.value);
                                setCommuneNom("");
                                setArrondissementNom("");
                              }}
                              disabled={loadingDepartements}
                              className="h-10 w-full rounded-[10px] border bg-white px-3 disabled:opacity-70"
                              style={{ borderColor: "#E5E7EB" }}
                            >
                              <option value="">{loadingDepartements ? "Chargement…" : "Département"}</option>
                              {departements.map((d) => (
                                <option key={d.id} value={d.id}>{d.nom}</option>
                              ))}
                            </select>
                            <select
                              value={communeNom}
                              onChange={(e) => {
                                setCommuneNom(e.target.value);
                                setArrondissementNom("");
                              }}
                              disabled={!departementId || loadingCommunes}
                              className="h-10 w-full rounded-[10px] border bg-white px-3 disabled:opacity-70"
                              style={{ borderColor: "#E5E7EB" }}
                            >
                              <option value="">
                                {!departementId ? "Choisir un département" : loadingCommunes ? "Chargement…" : "Commune"}
                              </option>
                              {communes.map((c) => (
                                <option key={`${c.id ?? c.nom}-${c.nom}`} value={c.nom}>{c.nom}</option>
                              ))}
                            </select>
                            <select
                              value={arrondissementNom}
                              onChange={(e) => setArrondissementNom(e.target.value)}
                              disabled={!communeNom || loadingArrondissements}
                              className="h-10 w-full rounded-[10px] border bg-white px-3 disabled:opacity-70"
                              style={{ borderColor: "#E5E7EB" }}
                            >
                              <option value="">
                                {!communeNom ? "Choisir une commune" : loadingArrondissements ? "Chargement…" : "Arrondissement"}
                              </option>
                              {arrondissements.map((a) => (
                                <option key={`${a.id ?? a.nom}-${a.nom}`} value={a.nom}>{a.nom}</option>
                              ))}
                            </select>
                          </div>
                          <input
                            value={quartierLibre}
                            onChange={(e) => setQuartierLibre(e.target.value)}
                            placeholder="Quartier (optionnel)"
                            className="h-10 w-full rounded-[10px] border px-3"
                            style={{ borderColor: "#E5E7EB" }}
                          />
                          {addressError && <p className="text-xs text-red-600">{addressError}</p>}
                          {composedAdresse && (
                            <p className="text-xs text-gray-500">Adresse enregistrée : {composedAdresse}</p>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1 font-semibold text-[#0B1F16]">{personal?.adresse || snapshot?.address || "—"}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === "medical" && (
                <div className="space-y-4">
                  <SectionHeader icon={Heart} title="Informations médicales" />
                  <p className="text-sm text-gray-600">
                    Données issues de votre dossier (lecture seule côté patient sauf mises à jour par votre équipe).
                  </p>
                  <dl className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                    <div className="flex items-start gap-3 rounded-[10px] border bg-[#FAFBFB] p-4" style={{ borderColor: "#E5E7EB" }}>
                      <Droplet size={16} className="mt-0.5 shrink-0 text-red-500" />
                      <div>
                        <dt className="text-gray-500">Groupe sanguin</dt>
                        <dd className="mt-1 font-semibold text-[#0B1F16]">{medical?.groupeSanguin || snapshot?.gs || "—"}</dd>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 rounded-[10px] border bg-[#FAFBFB] p-4" style={{ borderColor: "#E5E7EB" }}>
                      <TestTube2 size={16} className="mt-0.5 shrink-0 text-blue-500" />
                      <div>
                        <dt className="text-gray-500">Électrophorèse</dt>
                        <dd className="mt-1 font-semibold text-[#0B1F16]">{medical?.electrophorese || snapshot?.electro || "—"}</dd>
                      </div>
                    </div>
                    <div
                      className="flex items-start gap-3 rounded-[10px] border bg-[#FAFBFB] p-4 md:col-span-2"
                      style={{ borderColor: "#E5E7EB" }}
                    >
                      <Ruler size={16} className="mt-0.5 shrink-0 text-gray-400" />
                      <div>
                        <dt className="text-gray-500">Taille et poids</dt>
                        <dd className="mt-1 font-semibold text-[#0B1F16]">
                          {medical?.taille ? `${medical.taille} cm` : snapshot?.taille ? `${snapshot.taille} cm` : "—"}
                          {medical?.poids ? ` — ${medical.poids} kg` : snapshot?.poids ? ` — ${snapshot.poids} kg` : ""}
                        </dd>
                      </div>
                    </div>
                    <div
                      className="flex items-start gap-3 rounded-[10px] border bg-[#FAFBFB] p-4 md:col-span-2"
                      style={{ borderColor: "#E5E7EB" }}
                    >
                      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
                      <div>
                        <dt className="text-gray-500">Allergies</dt>
                        <dd className="mt-1 font-semibold text-[#0B1F16]">
                          {medical?.allergies?.length ? medical.allergies.join(", ") : snapshot?.allergies || "—"}
                        </dd>
                      </div>
                    </div>
                    <div
                      className="flex items-start gap-3 rounded-[10px] border bg-[#FAFBFB] p-4 md:col-span-2"
                      style={{ borderColor: "#E5E7EB" }}
                    >
                      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
                      <div>
                        <dt className="text-gray-500">Intolérances</dt>
                        <dd className="mt-1 font-semibold text-[#0B1F16]">
                          {medical?.intolerances?.length ? medical.intolerances.join(", ") : snapshot?.intolerances || "—"}
                        </dd>
                      </div>
                    </div>
                  </dl>
                </div>
              )}

              {activeSection === "documents" && (
                <div className="space-y-4">
                  <SectionHeader icon={FileDown} title="Export & vaccinations" />
                  <p className="text-sm text-gray-600">
                    Téléchargez la synthèse PDF de votre dossier médical ou votre carnet de vaccination.
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => void downloadMedicalPdf()}
                      disabled={pdfLoading || !patientNip}
                      className="flex h-12 items-center justify-center gap-2 rounded-[10px] border-2 font-semibold transition-colors hover:bg-[#FAFBFB] disabled:opacity-50"
                      style={{ borderColor: "#00553E", color: "#00553E", background: "white" }}
                    >
                      {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                      {pdfLoading ? "Génération…" : "Télécharger le dossier médical (PDF)"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void downloadVaccinationPdf()}
                      disabled={vaccinationPdfLoading || !patientNip}
                      className="flex h-12 items-center justify-center gap-2 rounded-[10px] font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-50"
                      style={{ background: "#00553E" }}
                    >
                      {vaccinationPdfLoading ? <Loader2 size={16} className="animate-spin" /> : <Syringe size={16} />}
                      {vaccinationPdfLoading ? "Génération…" : "Télécharger le carnet de vaccination (PDF)"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/patient/vaccinations")}
                    className="text-sm font-semibold text-[#00553E] hover:underline"
                  >
                    Voir le détail des vaccinations →
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

